import { Link } from 'react-router-dom'
import dayjs from 'dayjs'
import { Calendar } from 'lucide-react'

import {
    DataTableColumn,
    FilterItemConfig,
    FilterToggle,
} from '@/components/ui/data-table-customized'
import { Badge } from '@/components/ui/badge'
import { ItemModel } from '../../types/response'
import { paths } from '@/utils/path'

// === Columns ===

export const getModelColumns = (): DataTableColumn<ItemModel>[] => [
    {
        key: 'name',
        title: 'Name',
        width: 220,
        pinned: 'left',
        render: (_value, row) => (
            <Link
                to={`${paths.warehouseModels}/${row.id}`}
                className="text-blue-600 hover:underline font-medium"
            >
                {row.name}
            </Link>
        ),
    },
    {
        key: 'model',
        title: 'Model',
        width: 180,
        render: (value) => (
            <span className="text-sm">{(value as string) || '-'}</span>
        ),
    },
    {
        key: 'brand',
        title: 'Brand',
        width: 150,
        render: (value) => (
            <span className="text-sm">{(value as string) || '-'}</span>
        ),
    },
    {
        key: 'category',
        title: 'Category',
        width: 150,
        render: (value) => (
            <span className="text-sm capitalize">
                {(value as string) || '-'}
            </span>
        ),
    },
    {
        key: 'capacity',
        title: 'Capacity',
        width: 120,
        render: (value) => (
            <span className="text-sm">{(value as string) || '-'}</span>
        ),
    },
    {
        key: 'specifications',
        title: 'Specs',
        width: 100,
        render: (_value, row) => (
            <Badge variant="outline">{row.specifications?.length || 0}</Badge>
        ),
    },
    {
        key: 'is_active',
        title: 'Status',
        width: 120,
        render: (_value, row) => (
            <Badge variant={row.is_active ? 'default' : 'secondary'}>
                {row.is_active ? 'Active' : 'Inactive'}
            </Badge>
        ),
    },
    {
        key: 'created_at',
        title: 'Created',
        width: 150,
        render: (value) => {
            const date = dayjs(value as string)
            return (
                <div
                    className="flex items-center gap-2"
                    title={date.format('YYYY-MM-DD HH:mm:ss')}
                >
                    <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-sm">{date.format('YYYY-MM-DD')}</span>
                </div>
            )
        },
    },
]

// === Filters ===

const modelCategoryOptions = [
    { value: 'server', label: 'Server' },
    { value: 'switch', label: 'Switch' },
    { value: 'router', label: 'Router' },
    { value: 'firewall', label: 'Firewall' },
    { value: 'storage', label: 'Storage' },
    { value: 'ram', label: 'RAM' },
    { value: 'cpu', label: 'CPU' },
    { value: 'disk', label: 'Disk' },
    { value: 'psu', label: 'PSU' },
    { value: 'nic', label: 'NIC' },
    { value: 'other', label: 'Other' },
]

const modelStatusOptions = [
    { value: 'true', label: 'Active' },
    { value: 'false', label: 'Inactive' },
]

export const getModelFilterItems = (): FilterItemConfig[] => [
    {
        type: 'select',
        name: 'category',
        label: 'Category',
        placeholder: 'Filter by category',
        options: modelCategoryOptions,
    },
    {
        type: 'select',
        name: 'is_active',
        label: 'Status',
        placeholder: 'Filter by status',
        options: modelStatusOptions,
    },
]

// === Filter Toggles (column visibility) ===

export const getModelFilterToggles = (): FilterToggle[] => [
    { key: 'name', label: 'Name' },
    { key: 'model', label: 'Model' },
    { key: 'brand', label: 'Brand' },
    { key: 'category', label: 'Category' },
    { key: 'capacity', label: 'Capacity' },
    { key: 'specifications', label: 'Specs' },
    { key: 'is_active', label: 'Status' },
    { key: 'created_at', label: 'Created' },
]
